import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const W = { bg:'#0A1526', surface:'#16233F', border:'#28395C', muted:'#7d8ea2', bright:'#f1f5f9', accent:'#00A0C8', danger:'#fc8181', success:'#48bb78' };
const inp = { padding:'9px 12px', borderRadius:'7px', border:`1px solid ${W.border}`, background:W.bg, color:W.bright, fontSize:'14px', width:'100%', boxSizing:'border-box', outline:'none' };
const lbl = { fontSize:'11px', color:W.muted, fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'6px' };

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
function periodYears() { const y = new Date().getFullYear(); return [y-2, y-1, y, y+1]; }
function MonthYearPicker({ value, onChange, style }) {
  const [y, m] = value ? value.split('-') : [String(new Date().getFullYear()), String(new Date().getMonth()+1).padStart(2,'0')];
  const sel = { ...style, width:'auto', flex:1 };
  return (
    <div style={{ display:'flex', gap:'8px' }}>
      <select style={sel} value={m} onChange={e => onChange(`${y}-${e.target.value}`)}>
        {MONTHS.map((name, i) => { const val = String(i+1).padStart(2,'0'); return <option key={val} value={val}>{name}</option>; })}
      </select>
      <select style={sel} value={y} onChange={e => onChange(`${e.target.value}-${m}`)}>
        {periodYears().map(yr => <option key={yr} value={yr}>{yr}</option>)}
      </select>
    </div>
  );
}

function periodBounds(period) {
  const [y, m] = period.split('-').map(Number);
  const start = `${period}-01`;
  const end   = new Date(y, m, 1).toISOString().slice(0, 10);
  return { start, end };
}

function formatHrs(h) {
  const total = Math.round(h * 60);
  const hrs   = Math.floor(total / 60);
  const mins  = total % 60;
  return mins === 0 ? `${hrs}h` : `${hrs}h ${mins}m`;
}

export default function MonthEndWizard({ org, profile, clients, monthLocks, onClose, onRefresh, onNavigate, isContractor }) {
  const STEPS = ['Select Month', 'Review Clients', 'Close'];

  const defaultPeriod = new Date().toISOString().slice(0, 7);
  const [step, setStep]       = useState(0);
  const [period, setPeriod]   = useState(defaultPeriod);
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);
  const [done, setDone]       = useState(false);
  const [allClosed, setAllClosed] = useState(false);
  const [err, setErr]         = useState('');

  const [activity, setActivity] = useState(null);  // { clientId: hours }
  const [selected, setSelected] = useState({});     // { clientId: boolean }

  const isLocked = (clientId) =>
    (monthLocks || []).some(l => l.client_id === clientId && l.period === period && !l.reopened_at);

  const fetchActivity = async () => {
    setLoading(true);
    setErr('');
    const { start, end } = periodBounds(period);
    const { data, error } = await supabase
      .from('time_entries')
      .select('client_id, hours')
      .eq('org_id', org.id)
      .eq('user_id', profile.id)
      .gte('entry_date', start)
      .lt('entry_date', end);
    setLoading(false);
    if (error) { setErr(error.message); return; }
    const act = {};
    (data || []).forEach(e => {
      if (e.client_id) act[e.client_id] = (act[e.client_id] || 0) + parseFloat(e.hours || 0);
    });
    setActivity(act);
    // Pre-select all open clients (with or without activity)
    const sel = {};
    (clients || []).forEach(c => { if (!isLocked(c.id)) sel[c.id] = true; });
    setSelected(sel);
  };

  useEffect(() => {
    if (step === 1) fetchActivity();
  }, [step, period]); // eslint-disable-line

  // All assigned clients, split by lock state and activity
  const allClients        = clients || [];
  const openWithActivity  = allClients.filter(c => !isLocked(c.id) && activity && (activity[c.id] || 0) > 0);
  const openZero          = allClients.filter(c => !isLocked(c.id) && activity !== null && !(activity[c.id] > 0));
  const openClients       = [...openWithActivity, ...openZero];
  const lockedClients     = allClients.filter(c => isLocked(c.id));
  const selectedIds       = Object.keys(selected).filter(id => selected[id]);
  const allOpen           = openClients.length > 0 && openClients.every(c => selected[c.id]);

  const toggleAll = () => {
    if (allOpen) {
      setSelected({});
    } else {
      const sel = {};
      openClients.forEach(c => { sel[c.id] = true; });
      setSelected(sel);
    }
  };

  const closeSelected = async () => {
    setErr('');
    setClosing(true);
    for (const clientId of selectedIds) {
      const { error } = await supabase.rpc('close_client_month', {
        p_org: org.id, p_user: profile.id, p_client: clientId, p_period: period,
      });
      if (error) { setErr(`Close failed: ${error.message}`); setClosing(false); return; }
    }
    setClosing(false);
    onRefresh();
    // Determine if all assigned clients will now be closed after this action
    const remainingOpen = allClients.filter(c => !isLocked(c.id) && !selectedIds.includes(c.id));
    setAllClosed(remainingOpen.length === 0 && allClients.length > 0);
    setDone(true);
  };

  const [month, yearStr] = (() => {
    const [y, m] = period.split('-');
    return [new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleString('default', { month:'long' }), y];
  })();

  const stepContent = [
    <div key="month">
      <div style={{ marginBottom:'20px' }}>
        <div style={lbl}>Billing Month</div>
        <MonthYearPicker value={period} onChange={setPeriod} style={inp} />
        <div style={{ fontSize:'11px', color:W.muted, marginTop:'6px' }}>
          Select the month you want to close out. Closing locks all your entries for that period — they can be reopened if needed.
        </div>
      </div>
    </div>,

    <div key="review">
      {loading && <div style={{ color:W.muted, fontSize:'13px', padding:'30px', textAlign:'center' }}>Loading activity…</div>}
      {!loading && activity !== null && (
        <>
          {allClients.length === 0 && (
            <div style={{ color:W.muted, fontSize:'13px', padding:'30px', textAlign:'center' }}>
              No client assignments found.
            </div>
          )}
          {openClients.length > 0 && (
            <div style={{ marginBottom:'16px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
                <div style={lbl}>Open — select to close</div>
                <button onClick={toggleAll}
                  style={{ fontSize:'11px', color:W.accent, background:'none', border:'none', cursor:'pointer', padding:0 }}>
                  {allOpen ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                {openClients.map(c => {
                  const hrs = activity[c.id] || 0;
                  const isZero = hrs === 0;
                  return (
                    <div key={c.id} onClick={() => setSelected(s => ({ ...s, [c.id]: !s[c.id] }))}
                      style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 14px', borderRadius:'8px', cursor:'pointer', transition:'border-color 0.15s,background 0.15s',
                        border:`1px solid ${selected[c.id] ? W.accent : W.border}`,
                        background: selected[c.id] ? 'rgba(0,160,200,0.08)' : W.bg }}>
                      <div style={{ width:'16px', height:'16px', borderRadius:'4px', flexShrink:0, border:`2px solid ${selected[c.id] ? W.accent : '#3a4558'}`,
                        background: selected[c.id] ? W.accent : 'transparent', display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.15s' }}>
                        {selected[c.id] && <span style={{ color:'#fff', fontSize:'10px', fontWeight:'700', lineHeight:1 }}>✓</span>}
                      </div>
                      <span style={{ flex:1, fontSize:'14px', fontWeight:'600', color:W.bright }}>{c.name}</span>
                      <span style={{ fontSize:'12px', color: isZero ? W.muted : W.bright }}>
                        {isZero ? 'No entries — confirm $0' : formatHrs(hrs)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {lockedClients.length > 0 && (
            <div>
              <div style={{ ...lbl, marginBottom:'8px' }}>Already Locked</div>
              <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                {lockedClients.map(c => (
                  <div key={c.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 14px', borderRadius:'8px', border:`1px solid ${W.border}`, background:W.bg, opacity:0.6 }}>
                    <span style={{ fontSize:'14px', color:W.muted }}>🔒</span>
                    <span style={{ flex:1, fontSize:'14px', color:W.muted }}>{c.name}</span>
                    <span style={{ fontSize:'12px', color:W.muted }}>{formatHrs(activity[c.id] || 0)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {err && <div style={{ fontSize:'12px', color:W.danger, marginTop:'10px' }}>{err}</div>}
        </>
      )}
    </div>,

    <div key="close">
      <div style={{ padding:'16px', borderRadius:'9px', border:`1px solid ${W.border}`, background:W.bg, marginBottom:'14px' }}>
        <div style={{ fontSize:'13px', color:W.muted, marginBottom:'8px' }}>Closing {selectedIds.length} client{selectedIds.length !== 1 ? 's' : ''} for <strong style={{ color:W.bright }}>{month} {yearStr}</strong></div>
        {selectedIds.map(id => {
          const c = (clients || []).find(x => x.id === id);
          const hrs = activity?.[id] || 0;
          return c ? (
            <div key={id} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderTop:`1px solid ${W.border}` }}>
              <span style={{ fontSize:'13px', color:W.bright }}>{c.name}</span>
              <span style={{ fontSize:'12px', color:W.muted }}>{hrs > 0 ? formatHrs(hrs) : 'No entries — $0'}</span>
            </div>
          ) : null;
        })}
      </div>
      <div style={{ fontSize:'12px', color:W.muted, lineHeight:'1.5' }}>
        Entries will be locked and cannot be edited. You can reopen any client from My Logs if you need to make changes.
      </div>
      {err && <div style={{ fontSize:'12px', color:W.danger, marginTop:'10px' }}>{err}</div>}
    </div>,
  ];

  const doneContent = (
    <div style={{ textAlign:'center', padding:'12px 0' }}>
      <div style={{ fontSize:'36px', marginBottom:'12px' }}>🔒</div>
      <div style={{ fontSize:'16px', fontWeight:'700', color:W.bright, marginBottom:'6px' }}>Month closed!</div>
      <div style={{ fontSize:'13px', color:W.muted, marginBottom:'20px' }}>
        {selectedIds.length} client{selectedIds.length !== 1 ? 's' : ''} locked for <strong style={{ color:W.bright }}>{month} {yearStr}</strong>.
      </div>
      {isContractor && allClosed && onNavigate ? (
        <div style={{ marginBottom:'16px' }}>
          <div style={{ padding:'12px 16px', borderRadius:'8px', background:'rgba(0,160,200,0.08)', border:`1px solid rgba(0,160,200,0.3)`, marginBottom:'14px' }}>
            <div style={{ fontSize:'13px', fontWeight:'600', color:W.bright, marginBottom:'4px' }}>All clients confirmed — ready to submit!</div>
            <div style={{ fontSize:'12px', color:W.muted }}>Head to Member Invoices to review your line items and submit for approval.</div>
          </div>
          <button onClick={() => { onNavigate('statements'); onClose(); }}
            style={{ padding:'9px 24px', borderRadius:'7px', border:'none', background:W.accent, color:'#fff', fontSize:'13px', fontWeight:'600', cursor:'pointer', width:'100%', marginBottom:'8px' }}>
            Submit Invoice →
          </button>
          <button onClick={onClose}
            style={{ padding:'8px 24px', borderRadius:'7px', border:`1px solid ${W.border}`, background:'transparent', color:W.muted, fontSize:'13px', cursor:'pointer', width:'100%' }}>
            Done
          </button>
        </div>
      ) : (
        <button onClick={onClose}
          style={{ padding:'8px 24px', borderRadius:'7px', border:'none', background:W.accent, color:'#fff', fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>
          Done
        </button>
      )}
    </div>
  );

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1200, padding:'20px' }}>
      <div style={{ background:W.surface, border:`1px solid ${W.border}`, borderRadius:'14px', width:'100%', maxWidth:'480px', maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.6)' }}>
        <div style={{ padding:'22px 24px 16px', borderBottom:`1px solid ${W.border}`, flexShrink:0 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div>
              <div style={{ fontSize:'17px', fontWeight:'800', color:W.bright }}>Close Month</div>
              {!done && <div style={{ fontSize:'12px', color:W.muted, marginTop:'2px' }}>Step {step + 1} of {STEPS.length} — {STEPS[step]}</div>}
            </div>
            <button onClick={onClose} style={{ background:'none', border:'none', color:W.muted, fontSize:'20px', cursor:'pointer', lineHeight:1, padding:'2px' }}>×</button>
          </div>
          {!done && (
            <div style={{ marginTop:'14px', height:'4px', borderRadius:'2px', background:W.border, overflow:'hidden' }}>
              <div style={{ height:'100%', borderRadius:'2px', background:W.accent, width:`${((step + 1) / STEPS.length) * 100}%`, transition:'width 0.3s' }} />
            </div>
          )}
        </div>

        <div style={{ padding:'22px 24px', overflowY:'auto', flex:1 }}>
          {done ? doneContent : stepContent[step]}
        </div>

        {!done && (
          <div style={{ padding:'16px 24px', borderTop:`1px solid ${W.border}`, display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
            {step > 0
              ? <button onClick={() => { setErr(''); setStep(s => s - 1); }}
                  style={{ padding:'8px 18px', borderRadius:'7px', border:`1px solid ${W.border}`, background:'transparent', color:W.bright, fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>← Back</button>
              : <div />
            }
            {step < STEPS.length - 1
              ? <button onClick={() => { setErr(''); setStep(s => s + 1); }} disabled={step === 1 && loading}
                  style={{ padding:'8px 22px', borderRadius:'7px', border:'none', fontSize:'13px', fontWeight:'600',
                    background: (step === 1 && loading) ? W.border : W.accent,
                    color: (step === 1 && loading) ? W.muted : '#fff',
                    cursor: (step === 1 && loading) ? 'default' : 'pointer' }}>
                  Continue →
                </button>
              : <button onClick={closeSelected} disabled={closing || selectedIds.length === 0}
                  style={{ padding:'8px 22px', borderRadius:'7px', border:'none', fontSize:'13px', fontWeight:'600',
                    background: (closing || selectedIds.length === 0) ? W.border : W.accent,
                    color: (closing || selectedIds.length === 0) ? W.muted : '#fff',
                    cursor: (closing || selectedIds.length === 0) ? 'default' : 'pointer' }}>
                  {closing ? 'Closing…' : `Close ${selectedIds.length} Client${selectedIds.length !== 1 ? 's' : ''}`}
                </button>
            }
          </div>
        )}
      </div>
    </div>
  );
}
